import { Form, Button } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import marcaService from '../services/marcaService';
import searchService from '../services/searchService';

export default function Filters({ brands, models, onFilter, searchQuery, selectedMaker }) {
  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [anioDesde, setAnioDesde] = useState('');
  const [anioHasta, setAnioHasta] = useState('');
  const [tipoCombustible, setTipoCombustible] = useState('');
  const [precioDesde, setPrecioDesde] = useState('');
  const [precioHasta, setPrecioHasta] = useState('');
  const [region, setRegion] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [transmision, setTransmision] = useState('');
  
  // Estados para manejar marcas y modelos dependientes
  const [makersData, setMakersData] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [transmissions, setTransmissions] = useState([]);

  const availableCities = locations.find(l => l.region === region)?.cities?.map(c => c.name) || [];

  // Sincronizar marca cuando se selecciona desde el panel de marcas
  useEffect(() => {
    setMarca(selectedMaker || '');
  }, [selectedMaker]);

  // Cargar datos de marcas y opciones al montar el componente
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        const [makersData, options] = await Promise.all([
          marcaService.getMakers(),
          searchService.getSearchOptions(),
        ]);
        setMakersData(makersData);
        setLocations(options.locations || []);
        setTransmissions(options.transmissions || []);
      } catch (error) {
        console.error('Error al cargar opciones:', error);
        setMakersData(brands || []);
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, [brands]);

  // Actualizar modelos disponibles cuando cambia la marca seleccionada
  useEffect(() => {
    if (marca && makersData.length > 0) {
      const selectedMaker = makersData.find(maker => maker.name === marca);
      if (selectedMaker && selectedMaker.models) {
        setAvailableModels(selectedMaker.models);
      } else {
        setAvailableModels([]);
      }
    } else {
      setAvailableModels([]);
    }
    // Limpiar modelo seleccionado cuando cambia la marca
    setModelo('');
  }, [marca, makersData]);

  const formatCLP = (value) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    return new Intl.NumberFormat('es-CL').format(numericValue);
  };

  const styleButton = {
    textAlign: 'left',
    padding: '10px 0px',
    width: '90%',
    margin: '0 5%',
  };

  const styleDesdeHasta = {
    textAlign: 'center',
    padding: '10px 0px',
    width: '90%',
    margin: '0 5%',
  };

  const styleLabel = {
    marginLeft: '5%',
  };

  const button = {
    color: '#fff',
    backgroundColor: '#C31617',
    borderColor: '#C31617',
    margin: '5% 5% 0',
  };

  const handleFiltrar = async () => {
    try {
      // Construir filtros para el nuevo endpoint
      const searchFilters = {
        ...(searchQuery?.trim() ? { q: searchQuery.trim() } : {}),
        maker: marca || undefined,
        model: modelo || undefined,
        yearFrom: anioDesde || undefined,
        yearTo: anioHasta || undefined,
        fuel: tipoCombustible || undefined,
        priceMin: precioDesde ? parseInt(precioDesde.replace(/\./g, '')) : undefined,
        priceMax: precioHasta ? parseInt(precioHasta.replace(/\./g, '')) : undefined,
        region: region || undefined,
        city: ciudad || undefined,
        transmission: transmision || undefined,
        page: 0, // ✅ Página debe empezar en 0
        size: 5
      };

      // Limpiar filtros vacíos
      Object.keys(searchFilters).forEach(key => {
        if (searchFilters[key] === undefined || searchFilters[key] === '') {
          delete searchFilters[key];
        }
      });

      // Realizar búsqueda con el nuevo servicio
      const searchResults = await searchService.searchVehicles(searchFilters);
      
      // Llamar al callback con los resultados
      if (onFilter) {
        onFilter({
          results: searchResults.content,
          totalElements: searchResults.totalElements,
          totalPages: searchResults.totalPages,
          currentPage: searchResults.number + 1,
          filters: searchFilters
        });
      }
    } catch (error) {
      console.error('Error al realizar búsqueda:', error);
      // Fallback al método anterior si hay error - mantener formato consistente
      onFilter({
        results: [], // Array vacío para mantener consistencia
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        filters: {
          brand: marca,
          model: modelo,
          yearFrom: anioDesde,
          yearTo: anioHasta,
          fuelType: tipoCombustible,
          priceFrom: precioDesde.replace(/\./g, ''),
          priceTo: precioHasta.replace(/\./g, ''),
          region,
          city: ciudad,
          transmission: transmision,
        },
        error: error.message || 'Error en la búsqueda'
      });
    }
  };

  return (
    <>
      <h5>Filtros</h5>
      <div
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
        className="shadow p-2 mb-5 bg-white card-navigation-menu"
      >
        {/* Marca */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Marca</Form.Label>
          <Form.Select 
            style={styleButton} 
            value={marca} 
            onChange={(e) => setMarca(e.target.value)}
            disabled={loading}
          >
            <option value="">&nbsp;Todos</option>
            {makersData.map((item, index) => (
              <option key={`maker-${index}-${item.name}`} value={item.name}>
                {item.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Modelo */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Modelo</Form.Label>
          <Form.Select 
            style={styleButton} 
            value={modelo} 
            onChange={(e) => setModelo(e.target.value)}
            disabled={!marca || availableModels.length === 0}
          >
            <option value="">&nbsp;{marca ? 'Seleccione un modelo' : 'Seleccione primero una marca'}</option>
            {availableModels.map((model, index) => (
              <option key={`model-${index}-${model.name}`} value={model.name}>
                {model.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Año */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Año</Form.Label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Control
              style={styleDesdeHasta}
              type="number"
              placeholder="Desde"
              min="1900"
              max={new Date().getFullYear()}
              value={anioDesde}
              onChange={(e) => setAnioDesde(e.target.value)}
            />
            <Form.Control
              style={styleDesdeHasta}
              type="number"
              placeholder="Hasta"
              min="1900"
              max={new Date().getFullYear()}
              value={anioHasta}
              onChange={(e) => setAnioHasta(e.target.value)}
            />
          </div>
        </Form.Group>

        {/* Combustible */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Tipo Combustible</Form.Label>
          <Form.Select
            style={styleButton}
            value={tipoCombustible}
            onChange={(e) => setTipoCombustible(e.target.value)}
          >
            <option value="">&nbsp;Todos</option>
            <option value="Gasolina">Gasolina</option>
            <option value="Diésel">Diésel</option>
            <option value="Eléctrico">Eléctrico</option>
          </Form.Select>
        </Form.Group>

        {/* Precio */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Precio (CLP)</Form.Label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Control
              style={styleDesdeHasta}
              type="text"
              placeholder="Desde"
              value={precioDesde}
              onChange={(e) => setPrecioDesde(formatCLP(e.target.value))}
            />
            <Form.Control
              style={styleDesdeHasta}
              type="text"
              placeholder="Hasta"
              value={precioHasta}
              onChange={(e) => setPrecioHasta(formatCLP(e.target.value))}
            />
          </div>
        </Form.Group>

        {/* Región */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Región</Form.Label>
          <Form.Select style={styleButton} value={region} onChange={(e) => { setRegion(e.target.value); setCiudad(''); }}>
            <option value="">&nbsp;Todos</option>
            {locations.map(l => (
              <option key={l.region} value={l.region}>{l.region}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Ciudad */}
        <Form.Group className="mb-3">
          <Form.Label style={styleLabel}>Ciudad</Form.Label>
          <Form.Select style={styleButton} value={ciudad} onChange={(e) => setCiudad(e.target.value)} disabled={!region}>
            <option value="">&nbsp;Todos</option>
            {availableCities.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Transmisión */}
        <Form.Group>
          <Form.Label style={styleLabel}>Transmisión</Form.Label>
          <Form.Select style={styleButton} value={transmision} onChange={(e) => setTransmision(e.target.value)}>
            <option value="">&nbsp;Todos</option>
            {transmissions.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Botón Filtrar */}
        <div className="div-right">
          <Button variant="primary" style={button} onClick={handleFiltrar}>
            Filtrar
          </Button>
        </div>
      </div>
    </>
  );
}
